import axios, { AxiosRequestConfig } from "axios";
import { useBeamXRAuth } from "../auth/useBeamXRAuth";
import React, { useCallback } from "react";
import { HttpError } from "../types/Error";

// Base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL as string;
const DEBUG_OVERRIDE_API_BASE_URL = process.env.REACT_APP_DEBUG_OVERRIDE_API_BASE_URL as string;
const DEBUG_OVERRIDE_BEARER_TOKEN = process.env.REACT_APP_DEBUG_OVERRIDE_BEARER_TOKEN as string;

const useApiClient = () => {
  const { getAccessToken, renewAccessToken } = useBeamXRAuth();

  // Helper function to set up Axios config with Authorization header
  const getAxiosConfig = useCallback(async (): Promise<AxiosRequestConfig> => {

    if (DEBUG_OVERRIDE_API_BASE_URL && DEBUG_OVERRIDE_BEARER_TOKEN) {
      return {
        baseURL: DEBUG_OVERRIDE_API_BASE_URL,
        headers: {
          Authorization: DEBUG_OVERRIDE_BEARER_TOKEN,
          "Content-Type": "application/json",
        },
      };
    }

    // No local token cache. Both providers cache internally and renew in the background, and a
    // cache here would keep handing back a token they had already replaced.
    const token = await getAccessToken();

    return {
      baseURL: API_BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  }, [getAccessToken]);

  // Forces a brand new token. Callers use this after something changes the claims embedded in
  // it, such as creating a company, which adds a membership.
  const refreshToken = useCallback(async () => {
    try {
      return await renewAccessToken();
    } catch (error) {
      console.error("Failed to refresh token:", error);
    }
  }, [renewAccessToken]);

  // Function to handle errors and return an HttpError object
  const handleHttpError = (error: any): HttpError => {
    if (axios.isAxiosError(error) && error.response) {
      return {
        errorCode: error.response.status,
        description: error.response.data?.message || "An error occurred",
      };
    }
    return {
      errorCode: 500,
      description: "Unknown error",
    };
  };

  // GET request
  const get = useCallback(
    async <T = any,>(
      endpoint: string,
      config?: AxiosRequestConfig
    ): Promise<T | HttpError> => {
      try {
        const axiosConfig = await getAxiosConfig();
        const response = await axios.get<T>(endpoint, {
          ...axiosConfig,
          ...config,
        });
        return response.data;
      } catch (error) {
        return handleHttpError(error);
      }
    },
    [getAxiosConfig]
  );
  
  // POST form-data request.
  const postFormData = useCallback(
    async <T = any,>(
      endpoint: string,
      formData: FormData,
      config?: AxiosRequestConfig
    ): Promise<T | HttpError> => {
      try {
        const axiosConfig = await getAxiosConfig();
        const response = await axios.postForm<T>(endpoint, formData, {
          ...axiosConfig,
          ...config,
        });
        return response.data;
      } catch (error) {
        return handleHttpError(error);
      }
    },
    [getAxiosConfig]
  );

  const putFormData = useCallback(
    async <T = any,>(
      endpoint: string,
      formData: FormData,
      config?: AxiosRequestConfig
    ): Promise<T | HttpError> => {
      try {
        const axiosConfig = await getAxiosConfig();
        const response = await axios.putForm<T>(endpoint, formData, {
          ...axiosConfig,
          ...config,
        });
        return response.data;
      } catch (error) {
        return handleHttpError(error);
      }
    },
    [getAxiosConfig]
  );

  // POST request
  const post = useCallback(
    async <T = any,>(
      endpoint: string,
      data: any,
      config?: AxiosRequestConfig
    ): Promise<T | HttpError> => {
      try {
        const axiosConfig = await getAxiosConfig();
        const response = await axios.post<T>(endpoint, data, {
          ...axiosConfig,
          ...config,
        });
        return response.data;
      } catch (error) {
        return handleHttpError(error);
      }
    },
    [getAxiosConfig]
  );

  // DELETE request
  const del = useCallback(
    async <T = any,>(
      endpoint: string,
      config?: AxiosRequestConfig
    ): Promise<T | HttpError> => {
      try {
        const axiosConfig = await getAxiosConfig();
        const response = await axios.delete<T>(endpoint, {
          ...axiosConfig,
          ...config,
        });
        return response.data;
      } catch (error) {
        return handleHttpError(error);
      }
    },
    [getAxiosConfig]
  );

  // PUT request
  const put = useCallback(
    async <T = any,>(
      endpoint: string,
      data: any,
      config?: AxiosRequestConfig
    ): Promise<T | HttpError> => {
      try {
        const axiosConfig = await getAxiosConfig();
        const response = await axios.put<T>(endpoint, data, {
          ...axiosConfig,
          ...config,
        });
        return response.data;
      } catch (error) {
        return handleHttpError(error);
      }
    },
    [getAxiosConfig]
  );

  return { get, post, delete: del, put, refreshToken, postFormData, putFormData };
};

export default useApiClient;
